import React, { useEffect, useState } from 'react';
import { PRODUCT_TYPE } from '../config/api';
import useStore from '../store/useStore';
import Confetti from 'react-confetti';

const formatCoins = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '0';

  const hasDecimal = amount % 1 !== 0;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: hasDecimal ? 1 : 0,
    maximumFractionDigits: hasDecimal ? 1 : 0,
  }).format(amount);
};

const PaymentSuccess = ({ open, onClose, order }) => {
  const rewardTypeName = String(order?.rewardTypeName || 'Coins').toUpperCase();
  const isGemReward = Number(order?.productType) === PRODUCT_TYPE.NEW_STORE_GEM ||
    Number(order?.productType) === PRODUCT_TYPE.BASE_GEM ||
    rewardTypeName.includes('GEM');
  const rewardAmountClass = isGemReward
    ? 'text-[#198bc8] dark:text-[#16d4ff]'
    : 'text-[#a56e00] dark:text-[#ffd321]';
  const { user } = useStore();
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!order || !open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-overlay px-4 py-4 backdrop-blur-[5px]">
      {/* Confetti */}
      <div className="pointer-events-none fixed inset-0 z-[9999]">
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          numberOfPieces={200}
          recycle={false}
          colors={['#FFD700', '#FFA500', '#FF6347', '#00CED1', '#9370DB']}
          gravity={0.1}
        />
      </div>

      <section className="relative w-full max-w-xl overflow-hidden rounded-[2rem] border border-[#dfe7ff] bg-[linear-gradient(160deg,rgba(255,255,255,0.96),rgba(244,242,255,0.92))] shadow-[0_34px_70px_rgba(87,108,180,0.22)] dark:border-white/10 dark:bg-[linear-gradient(160deg,rgba(27,14,53,0.95),rgba(10,5,22,0.98))] dark:shadow-[0_34px_70px_rgba(0,0,0,0.45)]">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-[#dfe6ff] bg-white/72 font-display text-[1.35rem] font-black text-[#6f7487] transition hover:bg-white hover:text-[#1e1535] sm:right-4 sm:top-4 sm:h-11 sm:w-11 sm:text-[1.65rem] dark:border-white/10 dark:bg-white/5 dark:text-white/50 dark:hover:border-white/20 dark:hover:bg-white/10 dark:hover:text-white"
        >
          ×
        </button>

        <div className="px-4 py-10 text-center sm:px-8 sm:py-12">
          <p className="text-[10px] font-black uppercase tracking-[0.28em] text-emerald sm:text-[11px] sm:tracking-[0.34em]">
            Success
          </p>
          <h2 className="mt-3 font-display text-[1.6rem] font-black uppercase leading-[0.92] text-[#1e1535] sm:text-[2rem] dark:text-white">
            Purchase Successful!
          </h2>

          {/* Order summary */}
          <div className="mx-auto mt-5 max-w-xl rounded-[1.45rem] border border-[#c4a43a] bg-[linear-gradient(180deg,rgba(255,213,84,0.18),rgba(255,213,84,0.08))] p-4 shadow-[0_18px_36px_rgba(0,0,0,0.1)] sm:mt-6 sm:rounded-[1.7rem] sm:p-5 dark:border-[#7b6522] dark:bg-[linear-gradient(180deg,rgba(255,213,84,0.1),rgba(255,213,84,0.04))] dark:shadow-[0_18px_36px_rgba(0,0,0,0.15)]">
            <p className={`font-display text-[1.45rem] font-black uppercase leading-tight break-words sm:text-2xl sm:leading-none ${rewardAmountClass}`}>
              x {formatCoins(order.coins)} {rewardTypeName}
            </p>
            <p className="mt-2 text-xs text-[#6f7487] sm:text-sm dark:text-white/72">
              Order ID: {order.id}
            </p>
            <p className="text-xs text-[#6f7487] sm:text-sm dark:text-white/72">
              Amount Paid: ${order.amount}
            </p>
          </div>

          {/* Re-enter game prompt */}
          <div className="mx-auto mt-4 max-w-xl rounded-[1.1rem] border border-emerald/20 bg-emerald/8 p-3.5 sm:rounded-[1.2rem] sm:p-4 dark:border-[#1f6b59] dark:bg-[#0f2d29]/70">
            <p className="font-display text-xs font-black uppercase tracking-[0.05em] text-emerald sm:text-sm sm:tracking-[0.08em] dark:text-[#4de6b6]">
              Log in to the game to claim your rewards.
            </p>
          </div>

          <p className="mt-4 text-xs text-[#6f7487] sm:text-sm dark:text-white/70">
            Thank you for your purchase, {user?.name || 'Player'}!
          </p>

          <button
            type="button"
            onClick={onClose}
            className="mt-7 rounded-full border border-gold/40 bg-[linear-gradient(135deg,#ffd15d,#ffc44f)] px-6 py-3 font-display text-xs font-black uppercase tracking-[0.16em] text-[#2b1300] transition hover:brightness-105 sm:px-8 sm:text-sm sm:tracking-[0.22em]"
          >
            Continue Shopping
          </button>
        </div>
      </section>
    </div>
  );
};

export default PaymentSuccess;
